import React from 'react';
import Icon from '@mui/material/Icon';
import { Box } from '@mui/material';

interface FileIconProps {
  fileName?: string;
  contentType?: string;
  size?: 'small' | 'medium' | 'large';
  showBackground?: boolean;
}

const getFileIcon = (fileName?: string, contentType?: string): string => {
  const extension = fileName?.split('.').pop()?.toLowerCase();

  // Check by content type first
  if (contentType) {
    if (contentType.startsWith('image/')) return 'image';
    if (contentType === 'application/pdf') return 'picture_as_pdf';
    if (contentType.includes('spreadsheet') || contentType.includes('excel'))
      return 'table_chart';
    if (
      contentType.includes('presentation') ||
      contentType.includes('powerpoint')
    )
      return 'slideshow';
    if (contentType.includes('document') || contentType.includes('word'))
      return 'description';
    if (contentType.startsWith('video/')) return 'movie';
    if (contentType.startsWith('audio/')) return 'audio_file';
    if (contentType.startsWith('text/')) return 'text_snippet';
  }

  // Fallback to extension if fileName is available
  if (!fileName) return 'insert_drive_file';

  switch (extension) {
    case 'pdf':
      return 'picture_as_pdf';
    case 'doc':
    case 'docx':
      return 'description';
    case 'xls':
    case 'xlsx':
      return 'table_chart';
    case 'ppt':
    case 'pptx':
      return 'slideshow';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'webp':
      return 'image';
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
      return 'movie';
    case 'mp3':
    case 'wav':
    case 'flac':
      return 'audio_file';
    case 'txt':
    case 'md':
      return 'text_snippet';
    case 'zip':
    case 'rar':
    case '7z':
      return 'archive';
    default:
      return 'insert_drive_file';
  }
};

const getIconColor = (fileName?: string, contentType?: string): string => {
  const extension = fileName?.split('.').pop()?.toLowerCase();

  if (
    contentType?.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif'].includes(extension || '')
  ) {
    return '#4CAF50'; // Green for images
  }
  if (contentType === 'application/pdf' || extension === 'pdf') {
    return '#F44336'; // Red for PDFs
  }
  if (
    contentType?.includes('spreadsheet') ||
    ['xls', 'xlsx'].includes(extension || '')
  ) {
    return '#4CAF50'; // Green for spreadsheets
  }
  if (
    contentType?.includes('presentation') ||
    ['ppt', 'pptx'].includes(extension || '')
  ) {
    return '#FF9800'; // Orange for presentations
  }
  if (
    contentType?.includes('document') ||
    ['doc', 'docx'].includes(extension || '')
  ) {
    return '#2196F3'; // Blue for documents
  }
  if (
    contentType?.startsWith('video/') ||
    ['mp4', 'avi', 'mov'].includes(extension || '')
  ) {
    return '#9C27B0'; // Purple for videos
  }
  if (
    contentType?.startsWith('audio/') ||
    ['mp3', 'wav'].includes(extension || '')
  ) {
    return '#FF5722'; // Deep orange for audio
  }
  return '#757575'; // Gray for unknown types
};

const getSizeProps = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return { fontSize: 'small' as const, boxSize: 32 };
    case 'large':
      return { fontSize: 'large' as const, boxSize: 64 };
    default:
      return { fontSize: 'medium' as const, boxSize: 48 };
  }
};

const FileIcon: React.FC<FileIconProps> = ({
  fileName,
  contentType,
  size = 'medium',
  showBackground = true,
}) => {
  const iconName = getFileIcon(fileName, contentType);
  const iconColor = getIconColor(fileName, contentType);
  const { fontSize, boxSize } = getSizeProps(size);

  if (showBackground) {
    return (
      <Box
        sx={{
          width: boxSize,
          height: boxSize,
          borderRadius: 1,
          backgroundColor: `${iconColor}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${iconColor}40`,
        }}
      >
        <Icon fontSize={fontSize} sx={{ color: iconColor }}>
          {iconName}
        </Icon>
      </Box>
    );
  }

  return (
    <Icon fontSize={fontSize} sx={{ color: iconColor }}>
      {iconName}
    </Icon>
  );
};

export default FileIcon;
